import type { ReactNode } from 'react';
import { Select } from './ui';
import type { WorkingLocation } from '../lib/locationContext';

/**
 * Where a record belongs, on a form that records one.
 *
 * Deliberately quiet when there is nothing to decide: a business with one
 * location should not be asked a question with one answer, so the field renders
 * nothing and the form posts the resolved location. With several, it shows the
 * working location already selected and lets it be corrected for this record
 * only. With none, it says so — which is a real state for a member an owner has
 * not finished setting up, and better admitted here than as a failed save.
 */
export function LocationField({
  value,
  onChange,
  locations,
  error,
  label = 'Location',
}: {
  value: string;
  onChange: (next: string) => void;
  locations: WorkingLocation[];
  error?: string | undefined;
  label?: string;
}): ReactNode {
  if (locations.length === 0) {
    return (
      <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
        <p className="text-sm text-amber-900">
          You have not been given access to any location yet, so there is nowhere to file this.
          Ask an owner to assign you one under Shop → Locations.
        </p>
      </div>
    );
  }

  if (locations.length === 1) return null;

  return (
    <Select
      label={label}
      required
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Choose a location"
      options={locations.map((place) => ({
        value: place.id,
        label: place.type === 'external_mobile' ? `${place.name} (mobile)` : place.name,
      }))}
      {...(error ? { error } : {})}
    />
  );
}
